import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { Button, Group, Stack, Text, TextInput, useMantineTheme, ActionIcon, Avatar } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { Tabs, Tile as PlumTile } from "@quorumus/plum"
import { AiSearchLoadingGif } from "quorumdesign"

import { Icon, Tile, UnstyledButton, Grid } from "@/components"

import { checkCoPilotAnalyzeConditions } from "@/components/CopilotAnalyze/helpers"
import { generateAiSearchFiltersForMobile, getSearchLinks } from "@/pages/home/components/helpers"
import { handleSegueSearch } from "@/components/TopNavigation/components/SearchPopdown/FavoritesRecentsTabs/helpers"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { permissionFilter } from "shared/imports/permissionFunctions"
import urls from "app/static/frontend/imports/desktopPaths"

import { useStyles } from "@/pages/home/components/MobileLandingPage.styles"

import { CopilotTile } from "@/pages/home/components/CopilotTile"
import { CopilotMobileHomeButton } from "@/components/CopilotAnalyze/CopilotMobileHomeButton"
import { UserAction } from "@/components/TopNavigation/types"
import type { AvatarVariant } from "@/components/CopilotAnalyze/types"

export const MobileLandingPage = ({
    segue,
    segueToSearch,
    segueToProfile,
    setSearchTerm,
    clearAllFilterableListViews,
    loadUserActions,
    archiveUserAction,
    recents = [],
}: {
    segue: (path: string) => void
    segueToSearch: ({ initialFilter, quorumDataType }: { initialFilter: any; quorumDataType: any }) => void
    segueToProfile: ({ id }: { id: number }, value: number) => void
    setSearchTerm: (searchTerm: string) => void
    clearAllFilterableListViews: () => void
    loadUserActions: () => void
    archiveUserAction: (userAction: UserAction) => void
    recents: UserAction[]
}) => {
    const theme = useMantineTheme()
    const { classes } = useStyles()
    const [isAiSearching, setIsAiSearching] = useState(false)
    const [activeTab, setActiveTab] = useState<string>("search")

    useEffect(() => {
        setSearchTerm(null)
        clearAllFilterableListViews()
        isFeatureEnabled("ff_pwa_homepage") && loadUserActions()
    }, [setSearchTerm, clearAllFilterableListViews, loadUserActions])

    const submitSearch: React.FormEventHandler<HTMLFormElement> = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        const query = (event.currentTarget.elements[0] as HTMLInputElement).value

        if (isFeatureEnabled("ff_pwa_ai_search")) {
            setIsAiSearching(true)
            const { errorMessage, initialFilter, aiSearchQdt } = await generateAiSearchFiltersForMobile(query)
            setIsAiSearching(false)

            if (errorMessage) {
                notifications.show({
                    id: "invalid-search",
                    title: "Unsupported Search",
                    message: <Text>{errorMessage}</Text>,
                    autoClose: false,
                    color: "red.0",
                    icon: <Icon icon="times" color={theme.colors.red[5]} />,
                    closeButtonProps: { "aria-label": "Dismiss notification" },
                })
            } else {
                setSearchTerm(query)
                segueToSearch({
                    initialFilter,
                    quorumDataType: aiSearchQdt,
                })
            }
        } else {
            setSearchTerm(query)
            segueToSearch({
                initialFilter: { advanced_search: query },
                quorumDataType: DjangIO.app.models.QuorumDataType.person.value,
            })
        }
    }

    const renderTabs = () => {
        return (
            <Tabs value={activeTab} classNames={{ root: classes.tabsRoot }}>
                <Tabs.List>
                    <Tabs.Tab value="search" onClick={() => setActiveTab("search")}>
                        Search
                    </Tabs.Tab>
                    <Tabs.Tab value="history" onClick={() => setActiveTab("history")}>
                        History
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="search" className={classes.pwaSearchLinks}>
                    {getSearchLinks().map((searchLink) => (
                        <Link
                            className={classes.pwaSearchLink}
                            to={`/search/${searchLink.quorumDataType.search_url}`}
                            key={searchLink.quorumDataType.value}
                        >
                            <Icon icon={searchLink.icon} />
                            {searchLink.label}
                        </Link>
                    ))}
                </Tabs.Panel>
                <Tabs.Panel value="history" className={classes.pwaRecentItems}>
                    {recents.length === 0 && (
                        <div className={classes.noRecentContainer}>
                            <Icon icon="history" className={classes.noRecentIcon} size="2x" />
                            <div>
                                <div className={classes.noRecentTitle}>No history yet</div>
                                <div className={classes.noRecentText}>
                                    Start making searches and visiting profiles to populate the history page.
                                </div>
                            </div>
                        </div>
                    )}
                    {recents.slice(0, 6).map((recent) => renderRecentItem(recent))}
                </Tabs.Panel>
            </Tabs>
        )
    }

    const renderRecentItem = (recent: UserAction) => {
        const dataType: any = DjangIO.app.models.QuorumDataType.by_value(recent.data_type)
        const displayImage = recent.person__image_url || recent.supporter__image_url

        let avatarVariant: AvatarVariant = "default"
        if (recent.person__most_recent_party === DjangIO.app.person.types.Party.Democrat.value) {
            avatarVariant = "democrat"
        } else if (recent.person__most_recent_party === DjangIO.app.person.types.Party.Republican.value) {
            avatarVariant = "republican"
        }

        return (
            <div className={classes.recentItemWrapper} key={recent.id}>
                <UnstyledButton
                    className={classes.recentItemButton}
                    onClick={() =>
                        handleSegueSearch(recent, dataType, setSearchTerm, segueToProfile, segueToSearch, null)
                    }
                >
                    <div className={classes.recentItemIconContainer}>
                        {displayImage === null ? (
                            <Icon icon={dataType.fa5_icon} iconFamily={dataType.fa5_icon_family || "fas"} />
                        ) : (
                            <Avatar size="sm" src={displayImage} variant={avatarVariant} />
                        )}
                    </div>
                    <Text className={classes.recentItemText}>{recent.humanized_text}</Text>
                </UnstyledButton>
                <ActionIcon
                    aria-label="Archive recent history item"
                    radius="xl"
                    size="xl"
                    onClick={() => {
                        archiveUserAction(recent)
                    }}
                >
                    <Icon icon="times" iconFamily="fal" />
                </ActionIcon>
            </div>
        )
    }

    const renderSearchLinks = () => {
        return (
            <div className={classes.searchLinks}>
                {getSearchLinks().map((searchLink) => (
                    <Link
                        className={classes.searchLink}
                        to={`/search/${searchLink.quorumDataType.search_url}`}
                        key={searchLink.quorumDataType.value}
                    >
                        <Icon icon={searchLink.icon} />
                        {searchLink.label}
                    </Link>
                ))}
            </div>
        )
    }

    const renderMainContent = () => {
        if (isAiSearching) {
            return (
                <Stack align="center">
                    <AiSearchLoadingGif />
                    <Text color="gray.7">Generating results...</Text>
                </Stack>
            )
        }

        if (isFeatureEnabled("ff_pwa_homepage")) {
            return renderTabs()
        }

        return renderSearchLinks()
    }

    const renderGroup = () => (
        <div className={classes.carousel}>
            <Group className={classes.quickActions} noWrap>
                {isFeatureEnabled("ff_ai_chat_history") && checkCoPilotAnalyzeConditions() && <CopilotTile />}
                {Userdata.should_display_interaction_form && (
                    <Tile
                        icon={<Icon icon="handshake" />}
                        orientation="vertical"
                        size="md"
                        variant="secondary"
                        onClick={() => segue(urls.logInteractionPrefix)}
                        data-plum-id="log-interaction-tile"
                    >
                        Log interaction
                    </Tile>
                )}
                {Userdata.should_display_relationship_form && (
                    <Tile
                        icon={<Icon icon="sync" />}
                        orientation="vertical"
                        size="md"
                        variant="secondary"
                        onClick={() => segue(urls.logRelationshipPrefix)}
                        data-plum-id="log-relationship-tile"
                    >
                        Relationship
                    </Tile>
                )}
                <Tile
                    icon={<Icon icon="comment-alt" />}
                    orientation="vertical"
                    size="md"
                    variant="secondary"
                    onClick={() => segue(urls.logCommentPrefix)}
                    data-plum-id="log-comment-tile"
                >
                    Log comment
                </Tile>
                {permissionFilter(DjangIO.app.models.QuorumDataType.supporter, {
                    regions: DjangIO.app.models.QuorumDataType.supporter.supported_regions,
                    useCurrentRegions: true,
                }) && (
                    <Tile
                        icon={<Icon icon="child" />}
                        orientation="vertical"
                        size="md"
                        variant="secondary"
                        onClick={() => segue(urls.editContactPrefix)}
                        data-plum-id="add-contact-tile"
                    >
                        Add contact
                    </Tile>
                )}
            </Group>
        </div>
    )

    const renderGrid = () => (
        <div>
            <Grid gutter="sm">
                {Userdata.should_display_interaction_form && (
                    <Grid.Col span={6} className={classes.gridCol}>
                        <PlumTile
                            icon={<Icon icon="handshake" />}
                            size="md"
                            w="100%"
                            onClick={() => segue(urls.logInteractionPrefix)}
                            data-plum-id="log-interaction-tile"
                        >
                            New interaction
                        </PlumTile>
                    </Grid.Col>
                )}
                {Userdata.should_display_relationship_form && (
                    <Grid.Col span={6} className={classes.gridCol}>
                        <PlumTile
                            icon={<Icon icon="sync" />}
                            size="md"
                            w="100%"
                            onClick={() => segue(urls.logRelationshipPrefix)}
                            data-plum-id="log-relationship-tile"
                        >
                            New relationship
                        </PlumTile>
                    </Grid.Col>
                )}
                {permissionFilter(DjangIO.app.models.QuorumDataType.supporter, {
                    regions: DjangIO.app.models.QuorumDataType.supporter.supported_regions,
                    useCurrentRegions: true,
                }) && (
                    <Grid.Col span={6} className={classes.gridCol}>
                        <PlumTile
                            icon={<Icon icon="child" />}
                            size="md"
                            w="100%"
                            onClick={() => segue(urls.editContactPrefix)}
                            data-plum-id="add-contact-tile"
                        >
                            New contact
                        </PlumTile>
                    </Grid.Col>
                )}
                <Grid.Col span={6} className={classes.gridCol}>
                    <PlumTile
                        icon={<Icon icon="comment-alt" />}
                        size="md"
                        w="100%"
                        onClick={() => segue(urls.logCommentPrefix)}
                        data-plum-id="log-comment-tile"
                    >
                        New comment
                    </PlumTile>
                </Grid.Col>
            </Grid>
        </div>
    )

    if (isFeatureEnabled("ff_pwa_homepage")) {
        return (
            <Stack className={classes.pwaContainer} data-plum-id="pwa-landing-page" spacing="0.25rem">
                <div className={classes.contentContainer}>
                    <Stack spacing="0.5rem">
                        <Text color="gray.7" size="xs">
                            QUICK ACTIONS
                        </Text>
                        {renderGrid()}
                    </Stack>
                    {renderMainContent()}
                </div>
                <div className={classes.lowerScreenButtonContainer}>
                    <CopilotMobileHomeButton />
                </div>
            </Stack>
        )
    }

    return (
        <Stack className={classes.container} data-plum-id="pwa-landing-page">
            <Stack spacing="0.5rem">
                <Text color="gray.7" size="xs">
                    QUICK ACTIONS
                </Text>
                {renderGroup()}
            </Stack>
            <form onSubmit={submitSearch}>
                <TextInput
                    icon={<Icon icon="search" color={theme.colors.violet[5]} />}
                    size="lg"
                    placeholder="Search..."
                    className={classes.searchInput}
                    data-plum-id="search-input"
                />
            </form>
            {renderMainContent()}
            <div className={classes.logout}>
                <Button
                    className={classes.logoutButton}
                    component="a"
                    href={urls.logout}
                    leftIcon={<Icon icon="arrow-from-left" color={theme.colors.red[9]} />}
                    color="red.9"
                    variant="subtle"
                    data-plum-id="logout-link"
                >
                    Log out
                </Button>
            </div>
        </Stack>
    )
}
